import React, { FC, useCallback, useEffect, useState } from 'react';

import { DateRange } from '@material-ui/pickers';

import { Grid, useMediaQuery, useTheme } from '@material-ui/core';

import { createStyles, makeStyles } from '@material-ui/core/styles';

import { AggregatedValues, ClientFilterType, HighlightTypes } from '../../../IBDashboard/IBDashboardModels';

import { getAggregatedValues } from '../../../IBDashboard/IBDashboardService';

import { CPQuickReport, DoubleGraphPanel, DoubleQuickReport, GraphPanel, QuickReport } from '../../../IBDashboard/components';

import { DateRangeSelect } from '../../../../components';
import { useApiError } from '../../../../providers';

import { useCommonStyles, variables } from '../../../../styles';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      width: '100%',
      margin: 0,
    },

    dateRangeContainer: {
      width: '30%',

      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },

    contentContainer: {
      width: '100%',
      margin: 0,
    },

    cardsColumn: {
      width: 'max-content',
      flexShrink: 0,
      overflowY: 'auto',
      maxHeight: '60vh',

      [theme.breakpoints.down('sm')]: {
        width: '100%',
        maxHeight: 'none',
        overflowY: 'visible',
      },
    },

    graphColumn: {
      minWidth: 0,
      overflow: 'visible',
    },

    mobileGraph: {
      width: '100%',
      minWidth: 0,
    },

    emptyState: {
      minHeight: 240,
      padding: 24,
    },

    compactCard: {
      '& .MuiCard-root': {
        display: 'flex',
        width: '100%',
        minWidth: 280,
        padding: '16px !important',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 8,
        alignSelf: 'stretch',
        borderRadius: 8,
        background: '#ffffff',
        height: 'auto !important',
        boxSizing: 'border-box',

        [theme.breakpoints.down('sm')]: {
          width: '100%',
          minWidth: 'unset',
        },
      },

      '& .MuiCardHeader-root': {
        padding: '0 !important',
        width: '100%',

        '& svg path': {
          fill: '#0080FE !important',
          stroke: 'none !important',
        },

        '& svg circle': {
          stroke: '#0080FE !important',
        },
      },

      '& .MuiCardContent-root': {
        display: 'flex !important',
        flexDirection: 'row !important',
        alignItems: 'center !important',
        flexWrap: 'wrap',
        gap: 8,
        padding: '0 !important',
        marginTop: '0 !important',
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
      },
    },

    dateRangeWrapper: {
      border: '1px solid rgba(3, 12, 33, 0.23)',
      borderRadius: 8,
    },

    dashboardContent: {
      width: '100%',
      margin: 0,
    },

    desktopCardsColumn: {
      width: 'max-content',
      flexShrink: 0,
      overflowY: 'auto',
      maxHeight: '60vh',
    },

    mobileCardsColumn: {
      width: '100%',
    },
  }),
);

interface Props {
  partnershipCode: string;
  translation: any;
  dateRange: DateRange<Date>;
  onDateRangeChange: (range: DateRange<Date>) => void;
}

const initialAggregatedValues: AggregatedValues = {
  accounts: 0,
  accountsPercent: 0,

  clients: 0,
  clientsPercent: 0,

  commissions: 0,
  commissionsChangePercent: 0,

  deposits: 0,
  depositsChangePercent: 0,

  withdrawals: 0,
  withdrawalsChangePercent: 0,

  netDeposits: 0,
  netDepositsChangePercent: 0,

  internalTransferIn: 0,
  internalTransferInChangePercent: 0,

  internalTransferOut: 0,
  internalTransferOutChangePercent: 0,

  netInternalTransfer: 0,
  netInternalTransferChangePercent: 0,

  volume: 0,
  volumeChangePercent: 0,

  volumeLots: 0,
  volumeLotsChangePercent: 0,

  profit: 0,
  profitChangePercent: 0,

  loss: 0,
  lossChangePercent: 0,

  updatedAt: '',
};

export const PartnershipDashboardTab: FC<Props> = ({ partnershipCode, translation, dateRange, onDateRangeChange }) => {
  const { addError } = useApiError();

  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedPanel, setSelectedPanel] = useState<string>('Commissions');

  const [aggregatedValues, setAggregatedValues] = useState<AggregatedValues>(initialAggregatedValues);

  const getValues = useCallback(
    async (selectedRange: DateRange<Date>) => {
      if (!selectedRange[0] || !selectedRange[1]) {
        return;
      }

      try {
        const response = await getAggregatedValues(partnershipCode, selectedRange[0], selectedRange[1], ClientFilterType.All);

        setAggregatedValues(response.data);
      } catch (error: any) {
        addError(JSON.stringify(error.response));
      }
    },
    [partnershipCode, addError],
  );

  useEffect(() => {
    getValues(dateRange);
  }, [dateRange, getValues]);

  const renderGraph = () => {
    const sharedProps = {
      clientFilterType: ClientFilterType.All,
      dateRange,
      partnership: partnershipCode,
    };

    switch (selectedPanel) {
      case 'Commissions':
        return (
          <GraphPanel
            {...sharedProps}
            currency="USD"
            highlightType={HighlightTypes.Commissions}
            legend={translation.text_6293}
            emptyMessage={translation.text_6294}
          />
        );

      case 'Clients & Accounts':
        return (
          <DoubleGraphPanel
            {...sharedProps}
            colors={[variables.richBlack, variables.mediumSeaGreen]}
            valueType="count"
            emptyMessage={translation.clients_accounts_no_data}
            highlightTypes={[HighlightTypes.Clients, HighlightTypes.TradingAccounts]}
            legendFirstItem={translation.clients}
            legendSecondItem={translation.text_6388}
            order={{
              Clients: 1,
              TradingAccounts: 2,
            }}
          />
        );

      // case 'Trading Accounts Deposits':
      //   return (
      //     <GraphPanel
      //       {...sharedProps}
      //       currency="USD"
      //       highlightType={
      //         HighlightTypes.InternalTransferIn
      //       }
      //       legend={
      //         translation.trading_accounts_deposits
      //       }
      //       emptyMessage={
      //         translation.product_wallet_deposits_no_data
      //       }
      //     />
      //   );

      // case 'Trading Accounts Withdrawals':
      //   return (
      //     <GraphPanel
      //       {...sharedProps}
      //       currency="USD"
      //       highlightType={
      //         HighlightTypes.InternalTransferOut
      //       }
      //       legend={
      //         translation.trading_accounts_withdrawals
      //       }
      //       emptyMessage={
      //         translation.product_wallet_withdrawals_no_data
      //       }
      //     />
      //   );

      // case 'Net Trading Accounts Deposits':
      //   return (
      //     <GraphPanel
      //       {...sharedProps}
      //       currency="USD"
      //       highlightType={
      //         HighlightTypes.NetInternalTransfer
      //       }
      //       legend={
      //         translation.net_trading_accounts_deposits
      //       }
      //       emptyMessage={
      //         translation.net_product_wallet_deposits_no_data
      //       }
      //     />
      //   );

      case 'Portfolio Deposits':
        return (
          <GraphPanel
            {...sharedProps}
            currency="USD"
            highlightType={HighlightTypes.Deposits}
            legend={translation.deposits}
            emptyMessage={translation.deposits_no_data}
          />
        );

      case 'Portfolio Withdrawals':
        return (
          <GraphPanel
            {...sharedProps}
            currency="USD"
            highlightType={HighlightTypes.Withdrawals}
            legend={translation.text_1660}
            emptyMessage={translation.withdrawals_no_data}
          />
        );

      case 'Net Portfolio Deposits':
        return (
          <GraphPanel
            {...sharedProps}
            currency="USD"
            highlightType={HighlightTypes.NetDeposits}
            legend={translation.text_270}
            emptyMessage={translation.text_270}
          />
        );

      case 'Clients P&L':
        return (
          <DoubleGraphPanel
            {...sharedProps}
            colors={[variables.mediumSeaGreen, variables.americanPink]}
            emptyMessage={translation.client_profit_loss_no_data}
            highlightTypes={[HighlightTypes.Profit, HighlightTypes.Loss]}
            legendFirstItem={translation.text_1603}
            legendSecondItem={translation.text_1604}
            order={{
              Profit: 1,
              Loss: 2,
            }}
          />
        );

      case 'Closed Positions':
        return (
          <DoubleGraphPanel
            {...sharedProps}
            colors={[variables.richBlack, variables.mediumSeaGreen]}
            emptyMessage={translation.closed_positions_no_data}
            highlightTypes={[HighlightTypes.Volume, HighlightTypes.VolumeLots]}
            legendFirstItem={translation.text_811}
            legendSecondItem={translation.text_696}
            order={{
              Volume: 1,
              VolumeLots: 2,
            }}
          />
        );

      default:
        return null;
    }
  };

  const renderMobileGraph = (panelName: string) => (isMobile && selectedPanel === panelName ? <Grid item>{renderGraph()}</Grid> : null);

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>

        <div
          className={classes.dateRangeWrapper}
          style={{
            width: isMobile ? '100%' : '30%',
          }}
        >
          <DateRangeSelect defaultValue={dateRange} onRangeChange={onDateRangeChange} />
        </div>
      </Grid>

      <Grid container direction="row" spacing={2} className={classes.dashboardContent}>
        <Grid item className={isMobile ? classes.mobileCardsColumn : classes.desktopCardsColumn}>
          <Grid container direction="column" spacing={1}>
            <Grid item className={classes.compactCard}>
              <QuickReport
                title={translation.text_6277}
                tooltip={translation.total_commission}
                currency="USD"
                value={aggregatedValues.commissions}
                percentage={aggregatedValues.commissionsChangePercent}
                selected={selectedPanel === 'Commissions'}
                onClick={() => setSelectedPanel('Commissions')}
              />
            </Grid>

            {renderMobileGraph('Commissions')}

            <Grid item className={classes.compactCard}>
              <DoubleQuickReport
                title={translation.clients_accounts}
                tooltip={translation.clients_accounts_tooltip}
                firstValue={aggregatedValues.clients}
                firstPercentage={aggregatedValues.clientsPercent}
                secondValue={aggregatedValues.accounts}
                secondPercentage={aggregatedValues.accountsPercent}
                selected={selectedPanel === 'Clients & Accounts'}
                onClick={() => setSelectedPanel('Clients & Accounts')}
              />
            </Grid>

            {renderMobileGraph('Clients & Accounts')}

            {/* <Grid
                item
                className={
                  classes.compactCard
                }
              >
                <QuickReport
                  title={
                    translation.trading_accounts_deposits
                  }
                  tooltip={
                    translation.trading_accounts_deposits
                  }
                  currency="USD"
                  value={
                    aggregatedValues.internalTransferIn
                  }
                  percentage={
                    aggregatedValues.internalTransferInChangePercent
                  }
                  selected={
                    selectedPanel ===
                    'Trading Accounts Deposits'
                  }
                  onClick={() =>
                    setSelectedPanel(
                      'Trading Accounts Deposits',
                    )
                  }
                />
              </Grid> */}

            {/* {renderMobileGraph(
                'Trading Accounts Deposits',
              )} */}

            {/* <Grid
                item
                className={
                  classes.compactCard
                }
              >
                <QuickReport
                  title={
                    translation.trading_accounts_withdrawals
                  }
                  tooltip={
                    translation.trading_accounts_withdrawals
                  }
                  currency="USD"
                  value={
                    aggregatedValues.internalTransferOut
                  }
                  percentage={
                    aggregatedValues.internalTransferOutChangePercent
                  }
                  selected={
                    selectedPanel ===
                    'Trading Accounts Withdrawals'
                  }
                  onClick={() =>
                    setSelectedPanel(
                      'Trading Accounts Withdrawals',
                    )
                  }
                />
              </Grid> */}

            {/* {renderMobileGraph(
                'Trading Accounts Withdrawals',
              )}

              <Grid
                item
                className={
                  classes.compactCard
                }
              >
                <QuickReport
                  title={
                    translation.net_trading_accounts_deposits
                  }
                  tooltip={
                    translation.net_trading_accounts_deposits
                  }
                  currency="USD"
                  value={
                    aggregatedValues.netInternalTransfer
                  }
                  percentage={
                    aggregatedValues.netInternalTransferChangePercent
                  }
                  selected={
                    selectedPanel ===
                    'Net Trading Accounts Deposits'
                  }
                  onClick={() =>
                    setSelectedPanel(
                      'Net Trading Accounts Deposits',
                    )
                  }
                />
              </Grid>

              {renderMobileGraph(
                'Net Trading Accounts Deposits',
              )} */}

            <Grid item className={classes.compactCard}>
              <QuickReport
                title={translation.text_269}
                tooltip={translation.text_269}
                currency="USD"
                value={aggregatedValues.deposits}
                percentage={aggregatedValues.depositsChangePercent}
                selected={selectedPanel === 'Portfolio Deposits'}
                onClick={() => setSelectedPanel('Portfolio Deposits')}
              />
            </Grid>

            {renderMobileGraph('Portfolio Deposits')}

            <Grid item className={classes.compactCard}>
              <QuickReport
                title={translation.text_1660}
                tooltip={translation.text_1660}
                currency="USD"
                value={aggregatedValues.withdrawals}
                percentage={aggregatedValues.withdrawalsChangePercent}
                selected={selectedPanel === 'Portfolio Withdrawals'}
                onClick={() => setSelectedPanel('Portfolio Withdrawals')}
              />
            </Grid>

            {renderMobileGraph('Portfolio Withdrawals')}

            <Grid item className={classes.compactCard}>
              <QuickReport
                title={translation.text_270}
                tooltip={translation.text_270}
                currency="USD"
                value={aggregatedValues.netDeposits}
                percentage={aggregatedValues.netDepositsChangePercent}
                selected={selectedPanel === 'Net Portfolio Deposits'}
                onClick={() => setSelectedPanel('Net Portfolio Deposits')}
              />
            </Grid>

            {renderMobileGraph('Net Portfolio Deposits')}

            <Grid item className={classes.compactCard}>
              <CPQuickReport
                title={translation.text_811}
                translation={translation}
                tooltip={translation.closed_positions_tooltip}
                currency="USD"
                firstValue={aggregatedValues.volume}
                firstPercentage={aggregatedValues.volumeChangePercent}
                secondValue={aggregatedValues.volumeLots}
                secondPercentage={aggregatedValues.volumeLotsChangePercent}
                selected={selectedPanel === 'Closed Positions'}
                onClick={() => setSelectedPanel('Closed Positions')}
              />
            </Grid>

            {renderMobileGraph('Closed Positions')}

            <Grid item className={classes.compactCard}>
              <DoubleQuickReport
                title={translation.clientsProfitLoss}
                tooltip={translation.profit_loss_tooltip}
                currency="USD"
                firstValue={aggregatedValues.profit}
                firstPercentage={aggregatedValues.profitChangePercent}
                secondValue={aggregatedValues.loss}
                secondPercentage={aggregatedValues.lossChangePercent}
                selected={selectedPanel === 'Clients P&L'}
                onClick={() => setSelectedPanel('Clients P&L')}
              />
            </Grid>

            {renderMobileGraph('Clients P&L')}
          </Grid>
        </Grid>

        {!isMobile && (
          <Grid item xs className={classes.graphColumn}>
            {renderGraph()}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
